// Dynamic Property Schema Generator for Scene Editor
// Automatically generates schemas by introspecting object data

export type PropertyType =
  | 'number'
  | 'string'
  | 'boolean'
  | 'color'
  | 'select'
  | 'slider'
  | 'texture'
  | 'object';

export interface PropertySchema {
  /** Display name in inspector */
  label: string;
  /** Property key */
  key: string;
  /** Input type */
  type: PropertyType;
  /** Current value */
  value: any;
  /** Min value for number/slider */
  min?: number;
  /** Max value for number/slider */
  max?: number;
  /** Step for number/slider */
  step?: number;
  /** Options for select */
  options?: Array<{ value: any; label: string }>;
  /** Whether to show input alongside slider */
  showInput?: boolean;
  /** Group this property belongs to */
  group?: string;
  /** Nested path (e.g., 'config.starCount') */
  path?: string;
  /** Whether this is a nested object (will be recursively rendered) */
  isNested?: boolean;
  /** Children properties (for nested objects) */
  children?: PropertySchema[];
}

export interface ComponentSchema {
  /** Component name */
  name: string;
  /** Display name */
  displayName: string;
  /** Properties in this component */
  properties: PropertySchema[];
}

// ===============================================
// PROPERTY HINTS (optional customization)
// ===============================================

export interface PropertyHint {
  /** Override detected type */
  type?: PropertyType;
  /** Display label */
  label?: string;
  /** Min value */
  min?: number;
  /** Max value */
  max?: number;
  /** Step */
  step?: number;
  /** Select options */
  options?: Array<{ value: any; label: string }>;
  /** Show input for slider */
  showInput?: boolean;
  /** Group name */
  group?: string;
  /** Hide this property from inspector */
  hidden?: boolean;
}

export interface ObjectHints {
  [propertyPath: string]: PropertyHint;
}

// ===============================================
// HINTS REGISTRY (optional customization)
// ===============================================

/**
 * Default hints for common properties
 * These hints help the auto-generator create better UIs
 */
export const DEFAULT_HINTS: ObjectHints = {
  // Transform properties
  scale: { type: 'slider', min: 0.1, max: 10, step: 0.01, showInput: true },
  rotation: { type: 'slider', min: 0, max: Math.PI * 2, step: 0.01, showInput: true },
  alpha: { type: 'slider', min: 0, max: 1, step: 0.01, showInput: true },

  // Sprite properties
  texture: { type: 'texture' },
  tint: { type: 'color' },
  'anchor.x': { type: 'slider', min: 0, max: 1, step: 0.01, showInput: true, label: 'Anchor X' },
  'anchor.y': { type: 'slider', min: 0, max: 1, step: 0.01, showInput: true, label: 'Anchor Y' },

  // Space Background properties
  mood: {
    type: 'select',
    options: [
      { value: 'cosmic', label: 'Cosmic' },
      { value: 'neon', label: 'Neon' },
      { value: 'aurora', label: 'Aurora' },
      { value: 'fire', label: 'Fire' },
      { value: 'ice', label: 'Ice' },
      { value: 'void', label: 'Void' },
    ],
  },
  'config.starCount': {
    type: 'slider',
    min: 0,
    max: 200,
    step: 1,
    showInput: true,
    label: 'Star Count',
  },
  'config.starBrightness': {
    type: 'slider',
    min: 0,
    max: 3,
    step: 0.01,
    showInput: true,
    label: 'Star Brightness',
  },
  'config.nebulaIntensity': {
    type: 'slider',
    min: 0,
    max: 2,
    step: 0.01,
    showInput: true,
    label: 'Nebula Intensity',
  },

  // Common hidden properties
  id: { hidden: true },
  type: { hidden: true },
  children: { hidden: true },
  parent: { hidden: true },
  filters: { hidden: true }, // Handled separately
  textureName: { hidden: true }, // Internal property
};

/**
 * Property grouping definitions
 */
export const PROPERTY_GROUPS: Record<string, string[]> = {
  Object: [], // Name is shown in header, no need to edit it here
  Transform: ['x', 'y', 'scale', 'rotation', 'alpha', 'visible'],
  Sprite: ['texture', 'tint', 'anchor.x', 'anchor.y'],
  'Space Background': [
    'mood',
    'config.starCount',
    'config.starBrightness',
    'config.nebulaIntensity',
  ],
};

// ===============================================
// DYNAMIC SCHEMA GENERATOR
// ===============================================

/**
 * Auto-detect smart ranges for numeric properties
 */
function detectSmartRanges(
  key: string,
  value: number
): { min?: number; max?: number; step?: number; type: 'slider' | 'number' } {
  const lowerKey = key.toLowerCase();

  // Position properties (x, y)
  if (lowerKey === 'x' || lowerKey === 'y') {
    return { type: 'number' }; // No slider for positions
  }

  // Scale properties
  if (lowerKey.includes('scale') || lowerKey.includes('size')) {
    return { type: 'slider', min: 0.1, max: 10, step: 0.01 };
  }

  // Rotation properties (in radians)
  if (lowerKey.includes('rotation') || lowerKey.includes('angle')) {
    return { type: 'slider', min: 0, max: Math.PI * 2, step: 0.01 };
  }

  // Alpha/Opacity properties
  if (lowerKey.includes('alpha') || lowerKey.includes('opacity')) {
    return { type: 'slider', min: 0, max: 1, step: 0.01 };
  }

  // Strength/Intensity properties
  if (lowerKey.includes('strength') || lowerKey.includes('intensity')) {
    return { type: 'slider', min: 0, max: value > 2 ? 10 : 3, step: 0.01 };
  }

  // Speed properties
  if (lowerKey.includes('speed')) {
    return { type: 'slider', min: 0, max: 5, step: 0.1 };
  }

  // Radius/Distance properties
  if (lowerKey.includes('radius') || lowerKey.includes('distance')) {
    return { type: 'slider', min: 0, max: 100, step: 0.1 };
  }

  // Brightness properties
  if (lowerKey.includes('brightness')) {
    return { type: 'slider', min: 0, max: 3, step: 0.01 };
  }

  // Count properties (star count, etc)
  if (lowerKey.includes('count') || lowerKey.includes('samples')) {
    return { type: 'slider', min: 0, max: 200, step: 1 };
  }

  // Threshold properties
  if (lowerKey.includes('threshold')) {
    return { type: 'slider', min: 0, max: 1, step: 0.01 };
  }

  // Anchor properties (0-1 range)
  if (lowerKey.includes('anchor')) {
    return { type: 'slider', min: 0, max: 1, step: 0.01 };
  }

  // If value is between 0 and 1, probably a normalized value
  if (value >= 0 && value <= 1) {
    return { type: 'slider', min: 0, max: 1, step: 0.01 };
  }

  // If value is between 0 and 10, probably a small range
  if (value >= 0 && value <= 10) {
    return { type: 'slider', min: 0, max: 10, step: 0.1 };
  }

  // Default: just a number input
  return { type: 'number' };
}

/**
 * Automatically detect property type from value
 */
function detectPropertyType(value: any, key: string, hints?: PropertyHint): PropertyType {
  // Check hints first
  if (hints?.type) return hints.type;

  // Auto-detect based on value
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') {
    // Check if it's a color (hex number)
    if (key.toLowerCase().includes('color') || key === 'tint') {
      return 'color';
    }
    // Use smart detection for slider vs number
    const smartRanges = detectSmartRanges(key, value);
    return smartRanges.type;
  }
  if (typeof value === 'string') {
    if (key === 'texture') return 'texture';
    return 'string';
  }
  if (typeof value === 'object' && value !== null) {
    return 'object';
  }

  return 'string'; // fallback
}

/**
 * Create a property label from key
 */
function createLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Generate property schema from object property
 */
function generatePropertySchema(
  key: string,
  value: any,
  path: string,
  allHints: ObjectHints
): PropertySchema | null {
  // Get hints for this specific path
  const hints = allHints[path];

  // Check if property should be hidden
  if (hints?.hidden) return null;

  const type = detectPropertyType(value, key, hints);
  const label = hints?.label || createLabel(key);

  const schema: PropertySchema = {
    label,
    key,
    type,
    value,
    path,
  };

  // Apply hints or smart detection
  if (hints) {
    if (hints.min !== undefined) schema.min = hints.min;
    if (hints.max !== undefined) schema.max = hints.max;
    if (hints.step !== undefined) schema.step = hints.step;
    if (hints.options) schema.options = hints.options;
    if (hints.showInput !== undefined) schema.showInput = hints.showInput;
    if (hints.group) schema.group = hints.group;
  }

  // Auto-detect ranges for sliders if not provided by hints
  if (type === 'slider' && typeof value === 'number') {
    const smartRanges = detectSmartRanges(key, value);
    if (schema.min === undefined && smartRanges.min !== undefined) schema.min = smartRanges.min;
    if (schema.max === undefined && smartRanges.max !== undefined) schema.max = smartRanges.max;
    if (schema.step === undefined && smartRanges.step !== undefined) schema.step = smartRanges.step;
    // Show input by default for sliders
    if (schema.showInput === undefined) schema.showInput = true;
  }

  // Handle nested objects
  if (type === 'object' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
    schema.isNested = true;
    schema.children = [];

    for (const childKey in value) {
      const childPath = `${path}.${childKey}`;
      const childSchema = generatePropertySchema(childKey, value[childKey], childPath, allHints);
      if (childSchema) {
        schema.children.push(childSchema);
      }
    }

    // Don't show nested object if all children are hidden
    if (schema.children.length === 0) {
      return null;
    }
  }

  return schema;
}

/**
 * Automatically generate schema from object
 * This is the core of the data-driven approach!
 */
export function generateSchemaFromObject(obj: any, customHints?: ObjectHints): ComponentSchema[] {
  const allHints = { ...DEFAULT_HINTS, ...customHints };
  const components: ComponentSchema[] = [];
  const propertiesByGroup = new Map<string, PropertySchema[]>();

  // Generate property schemas
  for (const key in obj) {
    const value = obj[key];
    const path = key;

    const propSchema = generatePropertySchema(key, value, path, allHints);
    if (!propSchema) continue; // Skip hidden properties

    // Determine group
    const hint = allHints[path];
    let groupName = hint?.group || 'Properties';

    // Auto-group by predefined groups
    for (const [group, keys] of Object.entries(PROPERTY_GROUPS)) {
      if (keys.includes(key) || keys.includes(path)) {
        groupName = group;
        break;
      }
    }

    // Add to group
    if (!propertiesByGroup.has(groupName)) {
      propertiesByGroup.set(groupName, []);
    }
    propertiesByGroup.get(groupName)!.push(propSchema);
  }

  // Convert groups to components
  for (const [groupName, properties] of propertiesByGroup.entries()) {
    components.push({
      name: groupName.replace(/\s+/g, ''),
      displayName: groupName,
      properties,
    });
  }

  return components;
}

// ===============================================
// HELPER FUNCTIONS
// ===============================================

/**
 * Get property value from object using dot notation
 * e.g., getNestedValue(obj, 'config.starCount') => obj.config.starCount
 */
export function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Set property value in object using dot notation
 * e.g., setNestedValue(obj, 'config.starCount', 150) => obj.config.starCount = 150
 */
export function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  const target = keys.reduce((current, key) => {
    if (!current[key]) current[key] = {};
    return current[key];
  }, obj);
  target[lastKey] = value;
  return obj;
}
